import { Link, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { useT } from '../lib/i18n';
import { useSettings } from '../lib/store';
import { useSavedMenus, useSavedOrders } from '../lib/savedLists';
import { MenuListItem } from '../components/MenuListItem';
import { OrderListItem } from '../components/OrderListItem';
import { languageNativeName } from '../../shared/languages';

const HOME_MENU_CAP = 5;
const HOME_ORDER_CAP = 3;

export default function HomeScreen() {
  const navigate = useNavigate();
  const t = useT();
  const targetLanguage = useSettings((s) => s.targetLanguage);
  const allergenCount = useSettings((s) => s.hardFilterAllergens.length);

  const { list: menus, loading: menusLoading } = useSavedMenus();
  const { list: orders } = useSavedOrders();

  const recentMenus = menus.slice(0, HOME_MENU_CAP);
  const recentOrders = orders.slice(0, HOME_ORDER_CAP);
  const hasAny = menus.length > 0 || orders.length > 0;

  return (
    <div className="min-h-dvh flex flex-col safe-top safe-bottom container-app">
      <header
        className="flex items-center justify-between pt-3 pb-4"
        style={{ position: 'relative', zIndex: 10 }}
      >
        <Link
          to="/"
          aria-label="Home"
          style={{ textDecoration: 'none', display: 'inline-flex', padding: '4px 0' }}
        >
          <Logo size="sm" />
        </Link>
        <Link to="/me" aria-label="You" style={iconLinkStyle}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21c0-4 3.5-7 8-7s8 3 8 7" />
          </svg>
          <span style={iconLabelStyle}>{t('home.tab.you')}</span>
        </Link>
      </header>

      <main className="flex-1 flex flex-col" style={{ paddingTop: 8, gap: 24 }}>
        {!hasAny && !menusLoading && (
          <div
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 0',
            }}
          >
            <p
              style={{
                fontSize: 16,
                color: 'var(--color-text-secondary)',
                maxWidth: 280,
                lineHeight: 1.5,
                margin: 0,
                textAlign: 'center',
              }}
            >
              {t('home.empty')}
            </p>
          </div>
        )}

        {recentMenus.length > 0 && (
          <section>
            <SectionHeader label="Recent menus" total={menus.length} cap={HOME_MENU_CAP} />
            <ul style={listStyle}>
              {recentMenus.map((m) => (
                <MenuListItem key={m.menuId} menu={m} showNote={false} />
              ))}
            </ul>
          </section>
        )}

        {recentOrders.length > 0 && (
          <section>
            <SectionHeader label="Recent orders" total={orders.length} cap={HOME_ORDER_CAP} />
            <ul style={listStyle}>
              {recentOrders.map((o) => (
                <OrderListItem key={o.id} order={o} />
              ))}
            </ul>
          </section>
        )}
      </main>

      <div className="flex flex-col gap-2 pt-4">
        <button onClick={() => navigate('/scan')} className="btn-primary w-full">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
            <circle cx="12" cy="13" r="4" />
          </svg>
          {hasAny ? t('home.cta.scanNew') : t('home.cta.scan')}
        </button>
        <p
          style={{
            textAlign: 'center',
            fontSize: 12,
            color: 'var(--color-text-muted)',
            margin: '4px 0 0',
            lineHeight: 1.5,
          }}
        >
          {t('home.trust.translates').replace('{lang}', languageNativeName(targetLanguage))}
          {allergenCount > 0 && (
            <>
              {' · '}
              {allergenCount === 1
                ? t('home.trust.oneAllergen')
                : t('home.trust.manyAllergens').replace('{n}', String(allergenCount))}
            </>
          )}
        </p>
      </div>
    </div>
  );
}

interface SectionHeaderProps {
  label: string;
  total: number;
  cap: number;
}

// Always shows total count next to label and always links to /me. When more
// items exist than the home cap, the link reads "See all N →"; otherwise
// "Open in You →" reinforces that /me is where these things live.
function SectionHeader({ label, total, cap }: SectionHeaderProps) {
  const overflow = total > cap;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 10,
      }}
    >
      <h2
        style={{
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: 'var(--color-text-secondary)',
          margin: 0,
        }}
      >
        {label}
        <span style={{ marginLeft: 8, color: 'var(--color-text-muted)', fontWeight: 400 }}>
          {total}
        </span>
      </h2>
      <Link
        to="/me"
        style={{
          fontSize: 12,
          color: 'var(--color-accent)',
          textDecoration: 'none',
          fontWeight: 500,
        }}
      >
        {overflow ? `See all ${total} →` : 'Open in You →'}
      </Link>
    </div>
  );
}

const iconLinkStyle: React.CSSProperties = {
  minWidth: 44,
  minHeight: 44,
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 2,
  color: 'var(--color-primary)',
  textDecoration: 'none',
  borderRadius: 8,
};

const iconLabelStyle: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.02em',
};

const listStyle: React.CSSProperties = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};
